import { motion } from 'framer-motion'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useAuth } from '@/lib/auth'
import { signInWithKakao } from '@/lib/supabase'
import { useMe } from '@/features/auth/use-me'
import { Link, useRouter, useRouterState } from '@tanstack/react-router'
import { TopNav, activeTopNav } from './top-nav'
import { TYPE_PATH } from '@/features/grants/type-routes'
import { useTopNavActive } from './top-nav-active'
import { Bell, BellOutline, ChevronLeft, User } from '@/components/icons'
import { GRANT_SUPPORT_FILTERS, identityOf, type GrantSupportFilter } from '@zoxo/shared'
import { useUnreadCount } from '@/features/notifications/use-unread-count'

interface HeaderProps {
  title?: string
  showMenu?: boolean
}

/** 하단 탭 화면의 이름. 홈은 로고가 이름이라 null. */
function mobileTitleOf(path: string): string | null {
  const p = path.replace(/\/$/, '') || '/'
  if (p === '/news' || p.startsWith('/news/grants') || p.startsWith('/news/loans') || p.startsWith('/news/facilities') || p.startsWith('/news/programs')) return '지원사업'
  if (p === '/articles') return '아티클'
  if (p === '/posts') return '커뮤니티'
  if (p === '/mypage') return '마이페이지'
  return null
}

/**
 * 탭 안의 하위 화면 → 「< 지원사업」처럼 머리에 설 이름과, 앱 안에서 온 길이 없을 때 갈 곳. 아니면 null.
 *   상세 `/news/<id>` `/articles/<id>` `/posts/<id>` → 그 탭 목록. 공고는 `?tab=`(눌러 들어온 섹션)이 있으면 그 기둥 목록
 *     — 홈은 맨 `<a>` 라 앱 안 기록이 없어 늘 이 길로 온다. 홈 융자 칸 → 상세 → 꺾쇠 = `/news/loans`.
 *   수정 `/articles/<id>/edit` `/posts/<id>/edit`    → 그 글 상세
 *   글쓰기 `/articles/create` `/posts/create` · 비공개 `/articles/pending` → 그 탭 목록
 * 목록 주소(`/news/loans` 등)는 하위 화면이 아니다 — 꺾쇠 없이 탭 이름만.
 */
function mobileBackOf(path: string, tab?: unknown): { label: string; to: string } | null {
  const m = /^\/(news|articles|posts)\/([^/]+)(\/edit)?\/?$/.exec(path)
  if (!m) return null
  const [, section, seg = '', edit] = m
  if (section === 'news') {
    if (edit || ['grants', 'loans', 'facilities', 'programs'].includes(seg)) return null
    const pillar = GRANT_SUPPORT_FILTERS.includes(tab as GrantSupportFilter) ? (tab as GrantSupportFilter) : null
    return { label: '지원사업', to: pillar ? TYPE_PATH[pillar] : '/news' }
  }
  const label = section === 'articles' ? '아티클' : '커뮤니티'
  const list = `/${section}`
  return { label, to: edit ? `${list}/${seg}` : list }
}

export function Header({ title, showMenu = true }: HeaderProps) {
  const routerState = useRouterState()
  const currentPath = routerState.location.pathname
  // 공고 상세가 넘긴 대표 유형이 있으면 그것, 없으면 경로로.
  const topNavOverride = useTopNavActive()
  const topNavActive = topNavOverride ?? activeTopNav(currentPath)

  /*
    **모바일 앱 머리.** 하단 탭(홈·지원사업·아티클·마이페이지)으로 들어온 화면은 앱의 탭
    화면이다 — 로고 대신 화면 이름이 왼쪽에 굵게 선다. 탭이 아닌 화면(상세)은 로고.
    단 지원사업·아티클·커뮤니티의 하위 화면(상세·글쓰기·수정·비공개)은 「< 지원사업」처럼 그 탭 이름에
    뒤로 가기 꺾쇠를 단다. 작성 중이면 나가기 전에 한 번 묻는다(lib/use-leave-guard).
    누르면 앱 안에서 온 길이 있으면 뒤로(목록의 스크롤·필터가 살아 있다), 없으면 그 탭 목록으로. sm 이상은 언제나 로고.
  */
  const tabPaths = ['/', '/news', '/news/grants', '/news/loans', '/news/facilities', '/news/programs', '/articles', '/posts', '/mypage']
  const isTabPage = tabPaths.some((p) => currentPath === p || currentPath === `${p}/`) || /^\/news\/programs\/[a-z]+\/?$/.test(currentPath)
  const mobileTitle = isTabPage ? mobileTitleOf(currentPath) : null
  const mobileBack = mobileBackOf(currentPath, (routerState.location.search as { tab?: unknown }).tab)
  const router = useRouter()

  const { isAuthenticated } = useAuth()
  const me = useMe()
  const unread = useUnreadCount()





  return (
    <header className="sticky top-0 z-[500]">
      <div className="mx-auto max-w-4xl bg-surface px-4">
        <div className="flex h-12 items-center justify-between">
          <div className="flex items-center gap-1.5">
            {title ? (
              <h1 className="text-base font-medium">{title}</h1>
            ) : (
              <>
              {mobileTitle ? (
                <h1 className="text-lg font-bold text-text sm:hidden">{mobileTitle}</h1>
              ) : null}
              {mobileBack ? (
                <a
                  href={mobileBack.to}
                  onClick={(e) => {
                    if (!router.history.canGoBack()) return
                    e.preventDefault()
                    router.history.back()
                  }}
                  className="-ml-1 flex items-center gap-1 text-lg font-bold text-text sm:hidden"
                >
                  <ChevronLeft className="size-5" aria-hidden="true" />
                  {mobileBack.label}
                </a>
              ) : null}
              <Link
                to="/"
                className={cn('flex items-center gap-2 text-text', (mobileTitle || mobileBack) && 'hidden sm:flex')}
                aria-label="좋소 과수원 홈"
              >
                <img src="/logo.svg" alt="" width={18} height={24} className="h-6 w-auto" />
                {/*
                  한글이 먼저다. 독자가 이 사이트를 부르는 이름이 「좋소 과수원」
                  이고 영문은 도메인(zoxo.kr)을 설명하는 부수적 표기다.

                  **크기는 둘 다 18px 다.** 한글은 em 상자를 꽉 채우고 라틴은
                  x-높이가 낮아 같은 px 에서도 한글이 더 커 보인다. 19/14 는 그
                  광학 차이가 지정값 차이 위에 얹혀 실제보다 훨씬 벌어져 보였다.
                  같은 값으로 두면 눈에는 한글이 살짝 큰 정도로 읽힌다.

                  위계는 크기가 아니라 굵기(bold/regular)와 색(text/text-subtle)이
                  진다. 그 둘이 이미 「한글이 먼저」를 말한다.
                */}
                <div className="flex items-baseline gap-2 leading-none">
                  <div className="font-kirang text-[21px] text-text">좋소 과수원</div>
                  <div className="hidden font-kirang text-[21px] text-text-subtle sm:block">
                    Zoxo Orchard
                  </div>
                </div>
              </Link>
              </>
            )}
          </div>

          {showMenu && (
            <div className="relative flex items-center gap-1">
              {/*
                종: 알림이 없으면 윤곽선, 있으면 solid + 숫자 뱃지. 알림 백엔드는 아직 없다
                (§features/notifications/use-unread-count.ts).
              */}
              <Button
                variant="ghost"
                size="icon"
                className="relative transition-colors duration-300 hover:bg-surface-muted"
                title="알림"
                aria-label={unread > 0 ? `알림 ${unread}개` : '알림'}
              >
                {unread > 0 ? (
                  <>
                    <Bell className="h-6 w-6 text-text" />
                    <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#DF301C] px-1 text-[10px] font-bold leading-none text-white">
                      {unread > 99 ? '99+' : unread}
                    </span>
                  </>
                ) : (
                  <BellOutline className="h-6 w-6 text-text-muted" />
                )}
              </Button>

              {/*
                아바타: 로그인 상태를 보이고 마이페이지로 가는 단축키. 햄버거 메뉴는 없앴다 —
                지원사업·아티클·커뮤니티·마이페이지는 머리 알약과 하단 탭이 이미 맡는다.
                로그인 전이면 회색 사람 아이콘, 누르면 카카오 로그인.
              */}
              {isAuthenticated && me.data ? (
                <Link
                  to="/mypage"
                  title={`${me.data.nickname} · 마이페이지`}
                  aria-label="마이페이지"
                  className="flex size-8 items-center justify-center overflow-hidden rounded-full border border-gray-200 bg-surface-muted text-base transition-colors hover:border-gray-400"
                >
                  {me.data.avatar ? (
                    <img src={me.data.avatar} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <span aria-hidden>{identityOf(me.data.identity)?.emoji ?? '🐣'}</span>
                  )}
                </Link>
              ) : (
                <button
                  type="button"
                  title="로그인"
                  aria-label="로그인"
                  onClick={() => void signInWithKakao()}
                  className="flex size-8 items-center justify-center rounded-full border border-dashed border-gray-300 bg-white text-gray-400 transition-colors hover:border-gray-500 hover:text-gray-600"
                >
                  <User className="h-4 w-4" />
                </button>
              )}
            </div>
          )}
        </div>
        {/* 최상위 메뉴. */}
        <TopNav
          active={topNavActive}
          // 밑줄이 탭 사이를 미끄러진다 — 같은 layoutId 의 span 이 다음 탭에 다시 나타나면 framer 가 잇는다.
          underline={({ className }) => (
            <motion.span aria-hidden layoutId="top-nav-underline" className={className} transition={{ type: 'spring', stiffness: 500, damping: 40 }} />
          )}
          link={({ href, className, children }) => (
            <Link to={href} className={className}>
              {children}
            </Link>
          )}
        />
      </div>
    </header>
  )
}
